<template>
  <div class="recipe-direct-import" style="display:inline-block; margin-left:10px;">
    <button type="button" class="btn btn-primary" @click.prevent="dialog = true">
      Import direct (admin)
    </button>

    <el-dialog
      title="Import direct de recettes"
      :visible.sync="dialog"
      width="80%"
      :close-on-click-modal="false"
      :close-on-press-escape="!running"
      :show-close="!running"
      @closed="reset"
      append-to-body>

      <div v-if="step === 'upload'">
        <el-upload
          drag
          action=""
          :auto-upload="false"
          :on-change="onFileChange"
          :on-remove="onFileRemove"
          :file-list="fileList"
          :limit="1"
          accept=".xlsx,.xls,.csv">
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">Déposez le fichier ou <em>cliquez</em></div>
        </el-upload>
        <el-alert
          v-if="prepareError"
          :title="prepareError"
          type="error"
          :closable="false"
          style="margin-top:1rem"/>
      </div>

      <div v-else>
        <el-progress :percentage="progressPct" :status="progressStatus" />
        <p style="margin-top:0.5rem">
          {{ doneCount }} / {{ recipes.length }} traitées —
          <span style="color:#67c23a">{{ successCount }} succès</span>,
          <span style="color:#f56c6c">{{ errorCount }} erreur(s)</span>
        </p>

        <el-table :data="recipes" max-height="500" size="mini" stripe>
          <el-table-column prop="name" label="Recette" min-width="200"/>
          <el-table-column prop="user_id" label="User" width="100"/>
          <el-table-column prop="ingredient_count" label="Ingr." width="80"/>
          <el-table-column label="Statut" min-width="280">
            <template slot-scope="s">
              <el-tag v-if="s.row.state === 'pending'" type="info" size="mini">en attente</el-tag>
              <el-tag v-else-if="s.row.state === 'running'" type="warning" size="mini">en cours…</el-tag>
              <el-tag v-else-if="s.row.state === 'created'" type="success" size="mini">✓ créée</el-tag>
              <el-tag v-else-if="s.row.state === 'updated'" type="success" size="mini">✓ mise à jour</el-tag>
              <el-tooltip v-else-if="s.row.state === 'error'" :content="s.row.message || ''" placement="top">
                <el-tag type="danger" size="mini">✗ {{ truncate(s.row.message) }}</el-tag>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="" width="60">
            <template slot-scope="s">
              <a v-if="s.row.recipe_id" :href="`/calculateur/${s.row.recipe_id}`" target="_blank">voir</a>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer">
        <template v-if="step === 'upload'">
          <el-button @click="dialog = false">Annuler</el-button>
          <el-button type="primary" :loading="preparing" :disabled="!fileList.length" @click="prepare">
            Analyser
          </el-button>
        </template>
        <template v-else-if="step === 'ready'">
          <el-button @click="dialog = false">Annuler</el-button>
          <el-button type="primary" @click="run">Lancer l'import ({{ recipes.length }})</el-button>
        </template>
        <template v-else-if="step === 'running'">
          <el-button disabled>Import en cours…</el-button>
        </template>
        <template v-else>
          <el-button type="primary" @click="dialog = false">Fermer</el-button>
        </template>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
    name: 'RecipeDirectImport',
    data() {
        return {
            dialog: false,
            step: 'upload',     // upload | ready | running | done
            fileList: [],
            preparing: false,
            prepareError: null,
            sessionId: null,
            recipes: [],
        }
    },
    computed: {
        running() { return this.step === 'running' },
        doneCount() { return this.recipes.filter(r => r.state !== 'pending' && r.state !== 'running').length },
        successCount() { return this.recipes.filter(r => r.state === 'created' || r.state === 'updated').length },
        errorCount() { return this.recipes.filter(r => r.state === 'error').length },
        progressPct() {
            return this.recipes.length ? Math.round(this.doneCount / this.recipes.length * 100) : 0
        },
        progressStatus() {
            if (this.step !== 'done') return null
            return this.errorCount ? 'exception' : 'success'
        },
    },
    methods: {
        onFileChange(file) {
            this.fileList = [file]
            this.prepareError = null
        },
        onFileRemove() {
            this.fileList = []
        },
        async prepare() {
            this.preparing = true
            this.prepareError = null
            const fd = new FormData()
            fd.append('file', this.fileList[0].raw)
            try {
                const { data } = await axios.post('/pro/recipes/import-direct/prepare', fd)
                this.sessionId = data.session_id
                this.recipes = data.recipes.map(r => ({ ...r, state: 'pending', message: null, recipe_id: null }))
                this.step = 'ready'
            } catch (e) {
                const errors = e.response && e.response.data && e.response.data.errors
                if (errors && errors.file) {
                    this.prepareError = errors.file[0]
                } else {
                    this.prepareError = (e.response && e.response.data && e.response.data.message) || 'Erreur analyse fichier'
                }
                this.fileList = []
            } finally {
                this.preparing = false
            }
        },
        async run() {
            this.step = 'running'
            for (const recipe of this.recipes) {
                recipe.state = 'running'
                try {
                    const { data } = await axios.post('/pro/recipes/import-direct/process', {
                        session_id: this.sessionId,
                        index: recipe.index,
                    })
                    recipe.state = data.status
                    recipe.message = data.message || null
                    recipe.recipe_id = data.recipe_id || null
                } catch (e) {
                    recipe.state = 'error'
                    recipe.message = (e.response && e.response.data && e.response.data.message) || 'Erreur réseau'
                    if (e.response && e.response.status === 404) {
                        this.$message.error('Session expirée, relancez l\'import')
                        break
                    }
                }
            }
            this.step = 'done'
        },
        reset() {
            this.step = 'upload'
            this.fileList = []
            this.sessionId = null
            this.recipes = []
            this.prepareError = null
            this.preparing = false
        },
        truncate(s) {
            if (!s) return ''
            return s.length > 40 ? s.slice(0, 40) + '…' : s
        },
    },
}
</script>
